/* eslint-disable i18next/no-literal-string */
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select'
import {Switch} from '@/components/ui/switch'
import {DatePicker} from '@/components/shared/DatePicker'
import {AttributeValueType} from '@/types/attribute'
import type {AttributeDefinition} from '@/types/attribute-definition'
import {useValueSourceResolver} from './useValueSourceResolver'
import {getHintText, getInputConstraints} from '@/lib/attribute-validation'
import {useI18n} from '@/i18n'

interface AttributeValueInputProps {
    definition: AttributeDefinition
    value: string
    onChange: (value: string) => void
    onBlur?: () => void
    error?: string | null
    testId?: string
    showLabel?: boolean
    showHint?: boolean
    inline?: boolean
    disabled?: boolean
    readOnly?: boolean
    resolvedValues?: ResolvedValue[]
}

interface ResolvedValue {
    code: string
    label: string
}

/* eslint-disable i18next/no-literal-string -- attr- prefix for test IDs, CSS classes */
export function AttributeValueInput({definition, value, onChange, onBlur, error, testId, showLabel = true, showHint = true, inline, disabled, readOnly, resolvedValues}: AttributeValueInputProps) {
    const id = testId ?? `attr-${definition.name}`
    const i18n = useI18n()
    const hookResolved = useValueSourceResolver(definition)
    const entries = resolvedValues ?? hookResolved
    /* eslint-enable i18next/no-literal-string */
    const inputType = AttributeValueType.inputType(definition.valueType)
    const wrapClass = inline ? 'flex items-center gap-3' : 'space-y-1.5' // eslint-disable-line i18next/no-literal-string -- CSS classes
    const hint = getHintText(definition, i18n)
    const constraints = getInputConstraints(definition)

    const label = showLabel ? (
        <Label htmlFor={id} className={inline ? 'text-sm whitespace-nowrap' : 'text-sm'}>
            {definition.displayName || definition.name}
            {definition.required && <span className="text-destructive ml-0.5">*</span>}
        </Label>
    ) : null

    const errorHint = (
        <>
            {error && <p className="text-xs text-destructive" data-testid="validation-error">{error}</p>}
            {showHint && hint && !error && <p className="text-xs text-muted-foreground" data-testid="validation-hint">{hint}</p>}
        </>
    )

    if (entries.length > 0) {
        return (
            <div className={wrapClass}>
                {label}
                <Select value={value} onValueChange={onChange} onOpenChange={(open) => { if (!open) onBlur?.() }} disabled={disabled || readOnly}>
                    <SelectTrigger id={id} data-testid={id} className={readOnly ? 'cursor-default' : undefined}>
                        <SelectValue placeholder={definition.displayName || definition.name}/>
                    </SelectTrigger>
                    <SelectContent>
                        {entries.map(v => {
                            /* eslint-disable i18next/no-literal-string -- test ID prefix */
                            const optionId = definition.name === 'dataClassification'
                                ? `classification-${v.code}`
                                : `option-${v.code}`
                            /* eslint-enable i18next/no-literal-string */
                            return (
                                <SelectItem key={v.code} value={v.code} data-testid={optionId}>
                                    {v.label}
                                </SelectItem>
                            )
                        })}
                    </SelectContent>
                </Select>
                {errorHint}
            </div>
        )
    }

    if (inputType === 'switch') {
        return (
            <div className={wrapClass}>
                {label}
                <div className="flex items-center gap-2 pt-1">
                    <Switch
                        id={id}
                        data-testid={id}
                        checked={value === 'true'}
                        onCheckedChange={(checked) => onChange(String(checked))}
                        disabled={disabled || readOnly}
                        className={readOnly ? 'cursor-default' : undefined}
                    />
                </div>
                {errorHint}
            </div>
        )
    }

    if (inputType === 'date') {
        const ariaLabel = definition.displayName || definition.name
        return (
            <div className={wrapClass}>
                {label}
                <DatePicker
                    testId={id}
                    value={value}
                    onChange={readOnly ? () => undefined : onChange}
                    ariaLabel={ariaLabel}
                    placeholder={ariaLabel}
                    disabled={disabled || readOnly}
                />
                {errorHint}
            </div>
        )
    }

    const inputClassName = readOnly ? 'cursor-text focus-visible:ring-0 focus-visible:ring-offset-0' : undefined // eslint-disable-line i18next/no-literal-string -- CSS classes
    const commonProps = {
        id,
        'data-testid': id,
        value,
        onChange: readOnly ? undefined : (e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value),
        onBlur,
        placeholder: definition.displayName || definition.name,
        maxLength: constraints.maxLength,
        min: constraints.min,
        max: constraints.max,
        disabled,
        readOnly,
    }

    if (inputType === 'number') {
        return (
            <div className={wrapClass}>
                {label}
                <Input type="number" {...commonProps} className={inputClassName}/>
                {errorHint}
            </div>
        )
    }

    return (
        <div className={wrapClass}>
            {label}
            <Input type="text" {...commonProps} className={inputClassName}/>
            {errorHint}
        </div>
    )
}
